import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  getPrimaryLocation, 
  updateLocation, 
  BusinessLocation 
} from '../../../../firebase/services/locationService';

const GeneralSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  
  // Business settings state
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Fetch primary location on component mount
  useEffect(() => {
    const fetchPrimaryLocation = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        const primaryLocation = await getPrimaryLocation();
        
        if (primaryLocation) {
          setLocationId(primaryLocation.id);
          setBusinessInfo({
            name: primaryLocation.name || '',
            phone: primaryLocation.phone || '',
            email: primaryLocation.email || '',
            website: primaryLocation.website || '',
            address: primaryLocation.address || '',
            city: primaryLocation.city || '',
            state: primaryLocation.state || '',
            zip: primaryLocation.zipCode || ''
          });
        } else {
          setError('No primary business location found. Please set up a primary location first.');
        }
      } catch (err) {
        console.error('Error fetching primary location:', err);
        setError('Failed to load business settings. Please try again.');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchPrimaryLocation();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!locationId) {
      setError('No primary location ID found. Cannot save settings.');
      return;
    }
    
    setLoading(true);
    setSuccess(null);
    setError(null);
    
    try {
      // Map the form data to the BusinessLocation format
      const locationData: Partial<BusinessLocation> = {
        name: businessInfo.name,
        phone: businessInfo.phone,
        email: businessInfo.email,
        website: businessInfo.website,
        address: businessInfo.address,
        city: businessInfo.city,
        state: businessInfo.state,
        zipCode: businessInfo.zip
      };
      
      // Update the location in Firestore
      await updateLocation(locationId, locationData);
      
      setSuccess('General settings updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating business settings:', err);
      setError('Failed to update business settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle business info changes
  const handleBusinessInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Display loading state
  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        General Settings
      </Typography>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Business Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Business Name"
                      name="name"
                      value={businessInfo.name}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Phone Number"
                      name="phone"
                      value={businessInfo.phone}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email Address"
                      name="email"
                      type="email"
                      value={businessInfo.email}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Website"
                      name="website"
                      value={businessInfo.website}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Street Address"
                      name="address"
                      value={businessInfo.address}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="City"
                      name="city"
                      value={businessInfo.city}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="State"
                      name="state"
                      value={businessInfo.state}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="ZIP Code"
                      name="zip"
                      value={businessInfo.zip}
                      onChange={handleBusinessInfoChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default GeneralSettingsPage; 